import axios from "axios";
import { ProjectId, ProjectVersionId } from "./projects";
import { PriorityId, TicketId, TicketKey, TicketStatusId } from "./tickets";
import { FollowUpTypes } from "./followups";
import { UserActivity, UserActivityResponse, toUserActivity } from "./users";
import { parseDateOnly } from "../utils";

export type HomeRadarRow = {
    projectId: ProjectId;
    projectName: string;
    projectKey: string;
    versionId: ProjectVersionId;
    versionName: string;
    theme: string | null;
    startDate: Date | null;
    endDate: Date | null;
    freezeDate: Date | null;
    releaseDate: Date | null;
    openTickets: number;
    totalTickets: number;
};

export type HomeTicket = {
    id: TicketId;
    key: TicketKey;
    title: string;
    ticketStatusId: TicketStatusId;
    priorityId: PriorityId;
    dueDate: Date | null;
};

export type HomeFollowUp = {
    id: string;
    ticketKey: TicketKey;
    ticketTitle: string;
    endDate: Date | null;
    followUpType: FollowUpTypes;
};

export type HomeHiddenVersion = {
    projectId: ProjectId;
    projectName: string;
    versionId: ProjectVersionId;
    versionName: string;
};

export type HomeData = {
    radar: HomeRadarRow[];
    hiddenVersions: HomeHiddenVersion[];
    myTickets: HomeTicket[];
    followUps: HomeFollowUp[];
};

type HomeRadarRowResponse = Omit<
    HomeRadarRow,
    "startDate" | "endDate" | "freezeDate" | "releaseDate"
> & {
    startDate: string | null;
    endDate: string | null;
    freezeDate: string | null;
    releaseDate: string | null;
};

type HomeTicketResponse = Omit<HomeTicket, "dueDate"> & { dueDate: string | null };

type HomeFollowUpResponse = Omit<HomeFollowUp, "endDate"> & { endDate: string | null };

type HomeDataResponse = {
    radar: HomeRadarRowResponse[];
    hiddenVersions: HomeHiddenVersion[];
    myTickets: HomeTicketResponse[];
    followUps: HomeFollowUpResponse[];
};

function toDate(value: string | null): Date | null {
    return value ? new Date(value) : null;
}

// Version planning dates arrive as date-only "YYYY-MM-DD"; parse them as local
// midnight so the radar shows the same calendar day everywhere.
function toPlanningDate(value: string | null): Date | null {
    return value ? parseDateOnly(value) : null;
}

export type HomeApi = {
    getHome(): Promise<HomeData>;
    getActivity(page: number, perPage: number): Promise<UserActivity>;
};

export const homeApi: HomeApi = {
    async getActivity(page, perPage) {
        const { data } = await axios.get<UserActivityResponse>("/api/v0/home/activity", {
            params: { page, perPage },
        });

        return toUserActivity(data);
    },
    async getHome() {
        const { data } = await axios.get<HomeDataResponse>("/api/v0/home");

        return {
            radar: data.radar.map((r) => ({
                ...r,
                startDate: toPlanningDate(r.startDate),
                endDate: toPlanningDate(r.endDate),
                freezeDate: toPlanningDate(r.freezeDate),
                releaseDate: toPlanningDate(r.releaseDate),
            })),
            hiddenVersions: data.hiddenVersions,
            myTickets: data.myTickets.map((t) => ({ ...t, dueDate: toDate(t.dueDate) })),
            followUps: data.followUps.map((f) => ({ ...f, endDate: toDate(f.endDate) })),
        };
    },
};
